import { getErrorMessage } from '../errors';
import { sizeToString } from './string';
import { FileChecks } from '../validators/fileChecks';
import { FileExtension } from '../validators/fileTypes';

const MB = 1024 * 1024;

export type UploadRule = {
  // Name of the multipart field that contains the file
  field: string;
  extensions: FileExtension[];
  maxBytes: number;
  requiredMessage: string;
  // Allows dots in the file name (e.g. `Contract v1.2.pdf`). Otherwise names with more than one
  // extension (e.g. `logo.php.png`) are rejected
  allowMultipleDots?: boolean;
};

const IMAGE_EXTENSIONS: FileExtension[] = ['png', 'jpg', 'jpeg', 'gif', 'webp'];

const imageRule = (field: string, requiredMessage: string): UploadRule => ({
  field,
  extensions: IMAGE_EXTENSIONS,
  maxBytes: 5 * MB,
  requiredMessage,
});

export type UploadRuleName =
  | 'brandLogo'
  | 'brandImage'
  | 'eventImage'
  | 'venueImage'
  | 'feedBackground'
  | 'venueAttributes'
  | 'font'
  | 'groupFile'
  | 'translationImport';

export const UPLOAD_RULES: Record<UploadRuleName, UploadRule> = {
  brandLogo: {
    field: 'file',
    extensions: ['png', 'jpg', 'jpeg', 'webp'],
    maxBytes: 2 * MB,
    requiredMessage: 'Missing logo file',
  },
  brandImage: imageRule('file', 'Missing image file'),
  eventImage: imageRule('eventImage', 'Missing image file'),
  venueImage: imageRule('venueImage', 'Missing image file'),
  feedBackground: imageRule('file', 'Missing background image file'),
  venueAttributes: {
    field: 'attributesFile',
    extensions: ['pdf', 'doc', 'docx', 'xls', 'xlsx'],
    maxBytes: 10 * MB,
    requiredMessage: 'Missing attributes file',
    allowMultipleDots: true,
  },
  font: {
    field: 'file',
    extensions: ['ttf', 'otf', 'woff', 'woff2'],
    maxBytes: 5 * MB,
    requiredMessage: 'Missing font file',
  },
  groupFile: {
    field: 'file',
    extensions: [...IMAGE_EXTENSIONS, 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'msg'],
    maxBytes: 20 * MB,
    requiredMessage: 'Missing file',
    allowMultipleDots: true,
  },
  translationImport: {
    field: 'file',
    extensions: ['csv', 'xlsx'],
    maxBytes: 10 * MB,
    requiredMessage: 'Missing file',
    allowMultipleDots: true,
  },
};

/** Human readable summary of the rule, e.g. `PNG, JPG up to 2 MB` (for upload hints) */
export function describeUploadRule(rule: UploadRule): string {
  const types = rule.extensions.join(', ').toUpperCase();

  return `${types} up to ${sizeToString(rule.maxBytes)}`;
}

/** Value for the `accept` attribute of a file input, e.g. `.png,.jpg` */
export function acceptFor(rule: UploadRule): string {
  return rule.extensions.map(ext => `.${ext}`).join(',');
}

/**
 * Quick check of the name and size of a file, to give feedback before uploading it. Runs the same
 * checks as the server, except the mime type and content ones. Returns the error message or
 * undefined when the file is valid.
 */
export function getUploadError(rule: UploadRule, file: { name: string; size: number }) {
  try {
    if (!rule.allowMultipleDots) FileChecks.singleExtension(file);
    FileChecks.extension(file, rule.extensions);
    FileChecks.size(file, rule.maxBytes);

    return undefined;
  } catch (e) {
    return getErrorMessage(e);
  }
}